/**
 * Canonical URL policy and site-level identity.
 *
 * THE CANONICAL RULE, decided once here so nothing has to guess:
 *
 *   https  +  apex host (no www)  +  lowercase-as-routed  +  NO trailing slash
 *
 * Why no trailing slash: the router defines paths without one, every internal
 * <Link> emits them without one, and inbound links use them without one. The
 * codebase was previously split -- 30 canonical tags carried a trailing slash
 * and 10 did not -- which meant the sitemap and the canonical tags disagreed
 * about the address of the same page. `canonicalFor()` is the only sanctioned
 * way to build a canonical URL, so that split cannot reappear.
 *
 * The site root is the single exception: it is "https://nayakstutorials.com/",
 * because a bare origin with no path is not a valid page URL.
 */

const businessConfig = require("./businessConfig");

const ORIGIN = "https://nayakstutorials.com";

/**
 * Normalise any route into the one canonical absolute URL for that page.
 * Accepts "/about", "about", "/about/" and "https://nayakstutorials.com/about"
 * and returns "https://nayakstutorials.com/about" for all four.
 */
function canonicalFor(routePath) {
  if (!routePath) return `${ORIGIN}/`;

  let path = String(routePath).trim();

  // Tolerate a full URL being passed in.
  path = path.replace(/^https?:\/\/[^/]+/i, "");

  // Drop query strings and fragments -- neither belongs in a canonical.
  path = path.split("?")[0].split("#")[0];

  if (!path.startsWith("/")) path = `/${path}`;

  // Collapse duplicate slashes, then strip the trailing one.
  path = path.replace(/\/{2,}/g, "/").replace(/\/+$/, "");

  return path === "" ? `${ORIGIN}/` : `${ORIGIN}${path}`;
}

const siteConfig = {
  origin: ORIGIN,
  name: businessConfig.legalName,
  locale: "en_IN",
  language: "en-IN",

  /**
   * Default share image. Absolute URL, served from /public so it is crawlable
   * without a build hash. Programme pages override this where a genuinely
   * relevant image exists -- reusing one generic image everywhere is explicitly
   * what we are avoiding.
   */
  defaultOgImage: `${ORIGIN}/nayaks-logo.png`,
  defaultOgImageAlt: `${businessConfig.legalName} logo`,
  defaultOgImageWidth: 600,
  defaultOgImageHeight: 111,

  twitterCard: "summary_large_image",

  /**
   * Robots directive for ordinary indexable pages.
   *
   * max-snippet:-1 and max-image-preview:large matter for AI surfaces: they
   * remove the length cap on the text snippet an engine may quote and permit a
   * large image. Restricting them limits how much of the page an answer engine
   * is allowed to reproduce when citing it.
   */
  defaultRobots:
    "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1",

  /** For pages that must exist but must never rank. */
  noindexRobots: "noindex, follow",

  /** Stable JSON-LD node identifiers. */
  ids: {
    organization: `${ORIGIN}/#organization`,
    website: `${ORIGIN}/#website`,
    logo: `${ORIGIN}/#logo`,
    primaryLocation: `${ORIGIN}/#primary-location`,
  },

  canonicalFor,
};

module.exports = siteConfig;
